import { updateManufacturingLaborRateItem } from "@/lib/manufacturing/laborBillingClient";
import { guessLaborRateRecipeKind } from "@/lib/manufacturing/laborCompanyRateRecipe";
import { normalizeLaborMatchKey } from "@/lib/manufacturing/laborCompanyPayMatch";
import type { LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";
import { createManufacturingRecipe } from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

const UNCONFIRMED_NOTES =
  "Created from unmatched company labor rate. Confirm in Models and add BOM lines.";

export function findLaborRateRecipeByCode(
  recipes: readonly MfgRecipeSummary[],
  code: string
): MfgRecipeSummary | null {
  const key = normalizeLaborMatchKey(code);
  const hits = recipes.filter((r) => normalizeLaborMatchKey(r.code) === key);
  return hits.length === 1 ? hits[0] : null;
}

export async function linkLaborRateItemToRecipe(input: {
  rate_item_id: string;
  recipe: Pick<MfgRecipeSummary, "id" | "code">;
  pays_by: LaborPaysBy;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await updateManufacturingLaborRateItem({
    rate_item_id: input.rate_item_id,
    recipe_id: input.recipe.id,
    item_code: input.recipe.code,
    option_code: input.recipe.code,
    pays_by: input.pays_by,
  });
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export async function createUnconfirmedLaborRateModel(input: {
  item: { id: string; item_code: string; label: string; notes: string };
  pays_by: LaborPaysBy;
  recipes: readonly MfgRecipeSummary[];
}): Promise<{ ok: true; recipe: MfgRecipeSummary } | { ok: false; error: string }> {
  const created = await createManufacturingRecipe({
    code: input.item.item_code,
    name: input.item.label,
    kind: guessLaborRateRecipeKind(input.item.notes, input.item.label),
    needs_review: true,
    notes: UNCONFIRMED_NOTES,
  });
  const recipe = created.ok
    ? created.data.recipe
    : findLaborRateRecipeByCode(input.recipes, input.item.item_code);
  if (!recipe) {
    return { ok: false, error: created.ok ? "Recipe create returned no model." : created.error };
  }
  const linked = await linkLaborRateItemToRecipe({
    rate_item_id: input.item.id,
    recipe,
    pays_by: input.pays_by,
  });
  if (!linked.ok) return linked;
  return { ok: true, recipe };
}
